import type { FormikErrors, FormikProps } from 'formik';
import type { ReactNode } from 'react';
import type { FormikInputProps } from 'Components/frameworks/formik/FormikInput';
import type { LabeledIcon } from 'Components/frameworks/labeled-icons-list/LabeledIconsList';
import type { ProductMappingProps } from 'Components/widgets/product/Product.mapper';
import type { ProductProps } from 'Components/widgets/product/Product.utils';
import type { ServicesContextType } from 'Context/ServicesContext';
import type { popinTypes } from 'Components/widgets/accommodation-room/infos/constants';

export type DeepPartial<T> = T extends object ? { [P in keyof T]?: DeepPartial<T[P]> } : T;

/** Narrow cast for spec/story fixtures that intentionally omit optional fields. */
export const asPartial = <T>(value: DeepPartial<T> | Record<string, unknown>): T => value as T;

export type AccommodationRoomThemeKey = 'default' | 'medium' | 'large';

export type AccommodationRoomInfosThemeKey = AccommodationRoomThemeKey | 'column';

export type LabeledIconsListTheme = 'list' | 'default' | 'listCondensed' | 'comparator';

export type AccommodationDistributionAttendee = {
  id: string;
  index: number;
  firstName?: string;
  lastName?: string;
  type: string;
};

export type AccommodationDistribution = {
  adults: ReadonlyArray<AccommodationDistributionAttendee>;
  babies: ReadonlyArray<AccommodationDistributionAttendee> | null | undefined;
  children: ReadonlyArray<AccommodationDistributionAttendee> | null | undefined;
};

export type ReservationScheduleFormEntry = {
  isSelected: boolean;
  isPaid: boolean;
  isFullyPaid?: boolean;
  startDate: string;
  endDate: string;
  timeSlots?: ReadonlyArray<string>;
  remainingStock?: number;
};

export type ReservationAttendeeFormEntry = {
  id?: string;
  selected: boolean;
  isPaid: boolean;
  price?: number;
  schedules: ReadonlyArray<ReservationScheduleFormEntry>;
};

export type ReservationSlotFormEntry = {
  productInfoId: string;
  serviceId: string;
  isSelected: boolean;
  isPaid: boolean;
  isDisabled?: boolean;
  price: number;
};

export type ReservationBlockFormValues = {
  sameScheduleForAll: boolean;
  attendees: Record<string, ReservationAttendeeFormEntry>;
  wholeDurationOfStay: Record<string, boolean>;
  slots: Record<string, ReservationSlotFormEntry>;
};

export type ReservationFilterFormValues = {
  selectedFilterValueId: string;
};

export type ReservationBlockFormErrors = {
  attendees?: string;
  schedules?: string;
  slots?: string;
  sameScheduleForAll?: string;
  wholeDurationOfStay?: string;
};

export type LastAvailableScheduleState = {
  isVisible?: boolean;
  nb?: number;
};

export type ReservationFormikInputProps = Omit<FormikInputProps, 'label'> & {
  className?: string;
  label?: ReactNode;
  onChange?: (event: { target: { checked: boolean } }) => void;
  placeholderComponent?: (isChecked: boolean) => ReactNode;
};

export type AlternativeFilterAccommodationBase = {
  id: string;
  label: string;
  firstAlternativeId: string;
};

export type AlternativeFilterAccommodationWithIcon = AlternativeFilterAccommodationBase & {
  icon: LabeledIcon | null | undefined;
};

export type AccommodationFiltersFormProps = FormikProps<ReservationFilterFormValues>;

export type PopinType = (typeof popinTypes)[keyof typeof popinTypes];

export type BeTrackingStorage = {
  contextNumber?: string;
  roomFilters?: ReadonlyArray<{
    proposalId?: string;
    upsellCategory?: string;
    upsellRevenue?: number;
    upsellName?: string;
  }>;
  roomOptions?: {
    proposalId?: string;
    upsellRevenue?: number;
  };
};

export type ProposalPriceFee = {
  amount?: {
    formattedValue?: string;
    value?: number;
  };
  type?: string;
};

export type ResortAccommodationLabel = {
  id: string;
  label?: string;
};

export type ProductMappingPropsInput = DeepPartial<ProductMappingProps> | Record<string, unknown>;

export const asProductMappingProps = (props: ProductMappingPropsInput): ProductMappingProps =>
  props as ProductMappingProps;

export const asProductProps = (props: DeepPartial<ProductProps>): ProductProps =>
  props as ProductProps;

export const asServicesContext = (value: DeepPartial<ServicesContextType>): ServicesContextType =>
  value as ServicesContextType;

export const reservationFormError = (field: keyof ReservationBlockFormValues): string =>
  `${field}Required`;

export type ProductMapperConfig = {
  common?: {
    closed?: string;
    openingSoon?: string;
  };
  faceting?: {
    availabilities?: {
      available?: string;
      childcare?: string;
      months?: string;
      unavailable?: string;
      unknown?: string;
      urgentizationPlural?: string;
      urgentizationSingular?: string;
      year?: string;
      years?: string;
    };
    compare?: {
      selected?: string;
      selectResort?: string;
      unavailableThumbnail?: string;
    };
  };
  offerDiscount?: Record<string, string>;
  price?: {
    format?: Record<string, string>;
  };
  resort?: {
    startingPrice?: {
      dateFormat?: string;
    };
  };
};

export const getProductMapperConfig = (config?: Record<string, unknown>): ProductMapperConfig =>
  (config ?? {}) as ProductMapperConfig;

/** Cast ProductFlag price theme string to Price component theme key. */
export const asPriceThemeKey = (theme: string): 'column' | 'default' | 'inline' | 'inlineSmall' =>
  theme as 'column' | 'default' | 'inline' | 'inlineSmall';
